import React, { useState, useMemo, useEffect } from 'react';
import { XIcon, RepeatIcon, CalendarIcon, ClockIcon } from 'lucide-react';
import type {
  RecurrencePattern,
  RecurrenceEndType,
  RecurringReminderTemplate,
} from '../types/recurring-reminders';
import { calculateNextOccurrences, WEEKDAY_NAMES } from '../types/recurring-reminders';

interface RecurringReminderFormProps {
  onClose: () => void;
  onSubmit: (data: RecurringReminderFormData) => void | Promise<void>;
  initialData?: RecurringReminderTemplate;
  isLoading?: boolean;
  error?: string | null;
}

export interface RecurringReminderFormData {
  title: string;
  description?: string | null;
  recurrence_pattern: RecurrencePattern;
  recurrence_interval: number;
  weekdays?: number[] | null;
  day_of_month?: number | null;
  start_date: string;
  start_time: string;
  end_type: RecurrenceEndType;
  end_after_occurrences?: number | null;
  end_on_date?: string | null;
}

// Helper: Normalize time format to HH:MM (remove seconds if present)
const normalizeTimeFormat = (time: string): string => {
  const trimmed = time.trim();
  // If time has seconds (HH:MM:SS), remove them
  if (trimmed.match(/^\d{2}:\d{2}:\d{2}$/)) {
    return trimmed.substring(0, 5); // Extract HH:MM
  }
  return trimmed;
};

export function RecurringReminderForm({
  onClose,
  onSubmit,
  initialData,
  isLoading = false,
  error = null,
}: RecurringReminderFormProps) {
  const [formData, setFormData] = useState<RecurringReminderFormData>({
    title: initialData?.title || '',
    description: initialData?.description || '',
    recurrence_pattern: initialData?.recurrence_pattern || 'daily',
    recurrence_interval: initialData?.recurrence_interval || 1,
    weekdays: initialData?.weekdays || [1], // Default to Monday
    day_of_month: initialData?.day_of_month || 1,
    start_date: initialData?.start_date || new Date().toISOString().split('T')[0],
    start_time: initialData?.start_time ? normalizeTimeFormat(initialData.start_time) : '10:00',
    end_type: initialData?.end_type || 'never',
    end_after_occurrences: initialData?.end_after_occurrences || 10,
    end_on_date: initialData?.end_on_date || null,
  });

  const [showAdvanced, setShowAdvanced] = useState(
    (initialData?.recurrence_interval || 1) > 1
  );
  const [validationError, setValidationError] = useState<string | null>(null);

  // Calculate preview of next occurrences
  const previewOccurrences = useMemo(() => {
    try {
      const template: RecurringReminderTemplate = {
        ...formData,
        id: '',
        user_id: '',
        client_id: '',
        last_generated_date: null,
        instances_generated: 0,
        is_active: true,
        paused_at: null,
        created_at: '',
        updated_at: '',
        deleted_at: null,
      };
      return calculateNextOccurrences(template, 5);
    } catch (e) {
      return [];
    }
  }, [formData]);

  // Update weekdays when pattern changes
  useEffect(() => {
    if (formData.recurrence_pattern === 'weekly' && (!formData.weekdays || formData.weekdays.length === 0)) {
      setFormData((prev) => ({ ...prev, weekdays: [1] })); // Default to Monday
    }
  }, [formData.recurrence_pattern]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    // Validation
    if (!formData.title.trim()) {
      setValidationError('Title is required');
      return;
    }

    if (formData.recurrence_pattern === 'weekly' && (!formData.weekdays || formData.weekdays.length === 0)) {
      setValidationError('Select at least one day of the week');
      return;
    }

    if (formData.end_type === 'after_occurrences' && (!formData.end_after_occurrences || formData.end_after_occurrences < 1)) {
      setValidationError('Number of occurrences must be at least 1');
      return;
    }

    if (formData.end_type === 'on_date' && !formData.end_on_date) {
      setValidationError('End date is required');
      return;
    }

    // Sanitize data: Only include fields relevant to the current pattern and end type
    const sanitizedData: RecurringReminderFormData = {
      title: formData.title.trim(),
      description: formData.description?.trim() || null,
      recurrence_pattern: formData.recurrence_pattern,
      recurrence_interval: formData.recurrence_interval,
      start_date: formData.start_date,
      start_time: normalizeTimeFormat(formData.start_time),
      end_type: formData.end_type,
      // Pattern-specific fields
      weekdays: formData.recurrence_pattern === 'weekly' ? formData.weekdays : null,
      day_of_month: formData.recurrence_pattern === 'monthly' ? formData.day_of_month : null,
      // End type-specific fields
      end_after_occurrences: formData.end_type === 'after_occurrences' ? formData.end_after_occurrences : null,
      end_on_date: formData.end_type === 'on_date' ? formData.end_on_date : null,
    };

    onSubmit(sanitizedData);
  };

  const handleWeekdayToggle = (day: number) => {
    const current = formData.weekdays || [];
    const updated = current.includes(day)
      ? current.filter((d) => d !== day)
      : [...current, day].sort();
    setFormData({ ...formData, weekdays: updated });
  };

  const handlePresetClick = (preset: 'daily' | 'weekly' | 'monthly') => {
    if (preset === 'daily') {
      setFormData({ ...formData, recurrence_pattern: 'daily', recurrence_interval: 1 });
    } else if (preset === 'weekly') {
      setFormData({ ...formData, recurrence_pattern: 'weekly', recurrence_interval: 1, weekdays: [1] });
    } else if (preset === 'monthly') {
      setFormData({ ...formData, recurrence_pattern: 'monthly', recurrence_interval: 1, day_of_month: 1 });
    }
    setShowAdvanced(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <RepeatIcon className="h-5 w-5 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              {initialData ? 'Edit Recurring Reminder' : 'Create Recurring Reminder'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error display */}
          {(error || validationError) && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
              {error || validationError}
            </div>
          )}

          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Weekly Check-in"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="description"
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              rows={3}
              placeholder="Optional details..."
            />
          </div>

          {/* Quick Presets */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quick Presets
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handlePresetClick('daily')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  formData.recurrence_pattern === 'daily' && formData.recurrence_interval === 1
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Daily
              </button>
              <button
                type="button"
                onClick={() => handlePresetClick('weekly')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  formData.recurrence_pattern === 'weekly' && formData.recurrence_interval === 1
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Weekly
              </button>
              <button
                type="button"
                onClick={() => handlePresetClick('monthly')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  formData.recurrence_pattern === 'monthly' && formData.recurrence_interval === 1
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Monthly
              </button>
            </div>
          </div>

          {/* Advanced toggle */}
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            {showAdvanced ? '▼' : '▶'} Advanced Options
          </button>

          {/* Advanced: Custom Interval */}
          {showAdvanced && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Repeat every
              </label>
              <div className="flex gap-2 items-center">
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={formData.recurrence_interval}
                  onChange={(e) =>
                    setFormData({ ...formData, recurrence_interval: parseInt(e.target.value) || 1 })
                  }
                  className="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <select
                  value={formData.recurrence_pattern}
                  onChange={(e) =>
                    setFormData({ ...formData, recurrence_pattern: e.target.value as RecurrencePattern })
                  }
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="daily">day(s)</option>
                  <option value="weekly">week(s)</option>
                  <option value="monthly">month(s)</option>
                </select>
              </div>
            </div>
          )}

          {/* Weekly: Select Days */}
          {formData.recurrence_pattern === 'weekly' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                On days <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => handleWeekdayToggle(day)}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      formData.weekdays?.includes(day)
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {WEEKDAY_NAMES[day].slice(0, 3)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Monthly: Day of Month */}
          {formData.recurrence_pattern === 'monthly' && (
            <div>
              <label htmlFor="day_of_month" className="block text-sm font-medium text-gray-700 mb-1">
                On day of month <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="day_of_month"
                min="1"
                max="31"
                value={formData.day_of_month || 1}
                onChange={(e) =>
                  setFormData({ ...formData, day_of_month: parseInt(e.target.value) || 1 })
                }
                className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                For months with fewer days, the last day of the month will be used
              </p>
            </div>
          )}

          {/* Start Date & Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="start_date" className="block text-sm font-medium text-gray-700 mb-1">
                <CalendarIcon className="inline h-4 w-4 mr-1" />
                Start Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                id="start_date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label htmlFor="start_time" className="block text-sm font-medium text-gray-700 mb-1">
                <ClockIcon className="inline h-4 w-4 mr-1" />
                Time <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                id="start_time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          {/* End Options */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ends
            </label>
            <div className="space-y-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="end_type"
                  value="never"
                  checked={formData.end_type === 'never'}
                  onChange={(e) => setFormData({ ...formData, end_type: e.target.value as RecurrenceEndType })}
                  className="text-blue-600"
                />
                <span className="text-sm text-gray-700">Never</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="end_type"
                  value="after_occurrences"
                  checked={formData.end_type === 'after_occurrences'}
                  onChange={(e) => setFormData({ ...formData, end_type: e.target.value as RecurrenceEndType })}
                  className="text-blue-600"
                />
                <span className="text-sm text-gray-700">After</span>
                <input
                  type="number"
                  min="1"
                  value={formData.end_after_occurrences || ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? null : parseInt(e.target.value);
                    setFormData({ ...formData, end_after_occurrences: value });
                  }}
                  disabled={formData.end_type !== 'after_occurrences'}
                  className="w-20 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-100"
                />
                <span className="text-sm text-gray-700">occurrences</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="end_type"
                  value="on_date"
                  checked={formData.end_type === 'on_date'}
                  onChange={(e) => setFormData({ ...formData, end_type: e.target.value as RecurrenceEndType })}
                  className="text-blue-600"
                />
                <span className="text-sm text-gray-700">On</span>
                <input
                  type="date"
                  value={formData.end_on_date || ''}
                  onChange={(e) => setFormData({ ...formData, end_on_date: e.target.value })}
                  disabled={formData.end_type !== 'on_date'}
                  className="flex-1 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-100"
                />
              </label>
            </div>
          </div>

          {/* Preview */}
          {previewOccurrences.length > 0 && (
            <div className="bg-gray-50 rounded-md p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Next 5 occurrences:</h3>
              <ul className="space-y-1">
                {previewOccurrences.map((date, idx) => (
                  <li key={idx} className="text-sm text-gray-600">
                    • {date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })} at {formData.start_time}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  {initialData ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>{initialData ? 'Update Reminder' : 'Create Reminder'}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
